// Plain deterministic Monitoring outcomes. This component renders only the server-authored projection and
// searches only its authoritative visible title; it never derives a summary or exposes an opaque run id.

import { useMemo, useState } from 'react';
import { EmptyState, Panel, StatusPill } from '../primitives.tsx';
import {
  MONITORING_OUTCOME_SEARCH_LIMIT,
  searchMonitoringOutcomes,
} from '../monitoring-outcome-search.ts';
import type {
  MonitoringRunOutcome,
  MonitoringRunOutcomeView,
  MonitoringTerminalReason,
} from '../operate-projections.ts';
import type { Tone } from '../primitives.tsx';

const OUTCOME_TONE: Readonly<Record<MonitoringRunOutcome, Tone>> = {
  Running: 'neutral',
  Completed: 'go',
  Failed: 'blocked',
  Interrupted: 'attention',
};

/** Searchable outcome list shown before the optional low-level diagnostics. */
export function MonitoringOutcomeSummary({
  rows,
}: {
  readonly rows: readonly MonitoringRunOutcomeView[];
}): React.JSX.Element {
  const [query, setQuery] = useState('');
  const visible = useMemo(() => searchMonitoringOutcomes(rows, query), [query, rows]);
  return (
    <div className="space-y-4" data-monitoring-outcomes>
      <input
        type="search"
        className="cm-search"
        aria-label="Search monitored work by title"
        placeholder="Search by scenario, task or session title"
        maxLength={MONITORING_OUTCOME_SEARCH_LIMIT}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
      {visible.length > 0 ? (
        <ol className="space-y-3" aria-label="Monitored work outcomes">
          {visible.map((row) => (
            <li key={row.runId} data-monitoring-outcome="">
              <Panel>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-medium text-text" data-monitoring-title>
                      {row.title.available ? row.title.title : 'Title unavailable'}
                    </h3>
                    {!row.title.available ? (
                      <p className="mt-1 text-sm text-text-muted" data-monitoring-title-unavailable>
                        No authoritative visible title is available for this run.
                      </p>
                    ) : null}
                  </div>
                  <StatusPill tone={OUTCOME_TONE[row.outcome]}>{row.outcome}</StatusPill>
                </div>
                <p className="mt-3 text-sm text-text-muted" data-monitoring-effects>
                  {effectLabel(row)}
                </p>
                <TerminalReason reason={row.terminalReason} />
              </Panel>
            </li>
          ))}
        </ol>
      ) : (
        <EmptyState>No monitored work matches that visible title.</EmptyState>
      )}
    </div>
  );
}

function effectLabel(row: MonitoringRunOutcomeView): string {
  const effects = row.externalEffects;
  if (effects.kind === 'none') return 'External effects: none recorded.';
  const failed = effects.failed > 0 ? ` · ${String(effects.failed)} failed` : '';
  return `External effects: ${String(effects.completed)} completed · ${String(effects.refused)} refused${failed}.`;
}

function TerminalReason({
  reason,
}: {
  readonly reason: MonitoringTerminalReason;
}): React.JSX.Element | null {
  if (reason.kind === 'none') return null;
  const label = reason.category === 'failure' ? 'Failure reason' : 'Interruption reason';
  return (
    <p className="mt-2 text-sm text-text-muted" data-monitoring-terminal-reason>
      {label}: {reason.kind === 'available' ? reason.text : 'unavailable'}
    </p>
  );
}
